import type { SearchParams } from '@/server/searchParams.ts';
import type {
  CustomGroup,
  Filter,
  FilterDataSearchDynamic,
  FiltersGroup,
  FiltersGroups,
} from '@/app/common/filters/core/filtersTypes.ts';
import { filterTypeOf } from '@/app/common/filters/types/filterTypeBehaviour.ts';
import { filterDefinitionOf } from '@/app/common/filters/core/filterDefinitions.ts';
import { applyUpdatedFilters } from '@/app/common/filters/core/filterUtils.ts';
import getSearchParams from '@/app/components/search/common/getSearchParams.ts';
import { PERSISTENT_FILTER_PARAMS, URLParameters } from '@/app/common/urlParameters.ts';
import { removeAllFilters } from '@/app/components/search/components/Filters/utils.ts';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import { removeParameters } from '@/app/common/urlParametersUtils.ts';

const createFilterParams = (filter: Filter): SearchParams => {
  const definition = filterDefinitionOf(filter.name);
  if (!definition) return {};

  const value = filterTypeOf(filter).serialise(filter, { writeDefault: definition.writesDefault });

  return value === undefined ? {} : { [definition.urlParam]: value };
};

const joinValues = (current: string, value: string): string =>
  [...new Set([...current.split(','), ...value.split(',')])].join(',');

const mergeFilterParams = (result: SearchParams, params: SearchParams): SearchParams =>
  Object.entries(params).reduce(
    (merged, [param, value]) => ({ ...merged, [param]: param in merged ? joinValues(merged[param], value) : value }),
    result
  );

const ownedParams = (builtFilters: Filter[]): URLParameters[] =>
  builtFilters.map((filter) => filterDefinitionOf(filter.name)?.urlParam).filter((param) => param !== undefined);

type CreateUpdatedSearchParamsProps = {
  filtersGroups: FiltersGroups;
  updatedFiltersGroups: FiltersGroups;
};
const createUpdatedSearchParams = ({ filtersGroups, updatedFiltersGroups }: CreateUpdatedSearchParamsProps) => {
  const builtFilters = updatedFiltersGroups.flatMap((group) => (group as FiltersGroup).filters ?? []);
  const filtersSearchParams: SearchParams = updatedFiltersGroups.reduce(
    (result: SearchParams, filtersGroup: FiltersGroup | CustomGroup): SearchParams =>
      (filtersGroup as FiltersGroup).filters.map(createFilterParams).reduce(mergeFilterParams, result),
    {}
  );

  const searchParamsFresh = getSearchParams();
  const searchParamsWithoutOwnedFilters = removeParameters(searchParamsFresh, [
    URLParameters.PAGE,
    URLParameters.OFFSET,
    ...ownedParams(builtFilters),
  ]);

  const result = { ...searchParamsWithoutOwnedFilters, ...filtersSearchParams };

  // When the property name filter (accommodation name) has just been applied then remove the MAP_AREA url param (if present).
  const findPropertyNameFilter = (groups: FiltersGroups) =>
    groups.flatMap((g) => (g as FiltersGroup).filters ?? []).find((f) => f.name === FILTER_NAME.PROPERTY_NAME);

  const updatedPropertyName = findPropertyNameFilter(updatedFiltersGroups)?.data as FilterDataSearchDynamic | undefined;
  const currentPropertyName = findPropertyNameFilter(filtersGroups)?.data as FilterDataSearchDynamic | undefined;

  if (updatedPropertyName?.value && !currentPropertyName?.value) {
    delete result[URLParameters.MAP_AREA];
  }

  return result;
};

type CreateUpdatedFilterSearchParams = {
  filtersGroups: FiltersGroups;
  updatedFilters: Filter[];
};
export const createUpdatedFilterSearchParams = ({
  filtersGroups,
  updatedFilters,
}: CreateUpdatedFilterSearchParams): SearchParams => {
  const updatedFiltersGroups = applyUpdatedFilters({ prevFiltersGroups: filtersGroups, updatedFilters });
  return createUpdatedSearchParams({ filtersGroups, updatedFiltersGroups });
};

export const createResetFilterSearchParams = (): SearchParams => {
  const searchParamsFresh = getSearchParams();
  return removeAllFilters(searchParamsFresh, { exclude: PERSISTENT_FILTER_PARAMS });
};
